import {
  useHMSStore,
  useHMSActions,
  selectIsConnectedToRoom,
  selectLocalPeer,
  selectCameraStreamByPeerID,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectLocalAudioTrackID,
  selectLocalVideoTrackID,
  selectPeers,
  selectVideoTrackByPeerID,
} from '@100mslive/react-sdk';
import { useEffect, useRef, useCallback } from 'react';
import { colors } from '../constants/colors';

interface VideoPreviewProps {
  authToken: string | null;
  userName: string;
  isBroadcasting: boolean;
  onClaimBroadcast?: () => void;
}

export function VideoPreview({ authToken, userName, isBroadcasting, onClaimBroadcast }: VideoPreviewProps) {
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const localPeer = useHMSStore(selectLocalPeer);
  const videoTrack = useHMSStore(selectCameraStreamByPeerID(localPeer?.id));
  const isVideoEnabled = useHMSStore(selectIsLocalVideoEnabled);
  const localAudioTrackID = useHMSStore(selectLocalAudioTrackID);
  const localVideoTrackID = useHMSStore(selectLocalVideoTrackID);
  const peers = useHMSStore(selectPeers);

  // Find the remote broadcaster (the other seller device that's actively streaming)
  const remoteBroadcaster = peers.find(
    (peer) => !peer.isLocal && (peer.roleName === 'host' || peer.roleName === 'broadcaster') && peer.videoTrack
  );
  const remoteVideoTrack = useHMSStore(selectVideoTrackByPeerID(remoteBroadcaster?.id));
  const activeRemoteTrack = remoteVideoTrack?.enabled ? remoteVideoTrack : null;

  const isConnectedRef = useRef(isConnected);
  isConnectedRef.current = isConnected;

  const remoteBlurRef = useRef<HTMLVideoElement>(null);
  const localBlurRef = useRef<HTMLVideoElement>(null);

  // Mirror srcObject to blurred background instead of double-attaching
  const remoteVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (el && activeRemoteTrack) {
      hmsActions.attachVideo(activeRemoteTrack.id, el);
      const sync = () => {
        if (remoteBlurRef.current && el.srcObject) {
          remoteBlurRef.current.srcObject = el.srcObject;
        }
      };
      sync();
      el.addEventListener('loadedmetadata', sync, { once: true });
    }
  }, [activeRemoteTrack, hmsActions]);

  const localVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (el && videoTrack) {
      hmsActions.attachVideo(videoTrack.id, el);
      const sync = () => {
        if (localBlurRef.current && el.srcObject) {
          localBlurRef.current.srcObject = el.srcObject;
        }
      };
      sync();
      el.addEventListener('loadedmetadata', sync, { once: true });
    }
  }, [videoTrack, hmsActions]);

  useEffect(() => {
    if (authToken && !isConnected) {
      hmsActions.join({
        authToken: authToken,
        userName,
        settings: {
          isAudioMuted: true,
          isVideoMuted: false,
        },
        autoManageWakeLock: true,
      }).catch((err: unknown) => {
        const name = err instanceof Error ? err.name : String(err);
        if (name === 'CantAccessCaptureDevice') {
          console.warn('Camera permission denied. Joining without video.');
          hmsActions.join({
            authToken: authToken,
            userName,
            settings: {
              isAudioMuted: true,
              isVideoMuted: true,
            },
            autoManageWakeLock: true,
          }).catch((e: unknown) => console.error('HMS join failed:', e));
        } else {
          console.error('HMS join failed:', err);
        }
      });
    }
  }, [authToken, isConnected, hmsActions, userName]);

  // Mute/unmute local tracks based on broadcasting state
  useEffect(() => {
    if (!isConnected) return;
    if (isBroadcasting) {
      if (localVideoTrackID) hmsActions.setEnabledTrack(localVideoTrackID, true);
      // Keep audio muted by default — seller can unmute from controls
    } else {
      if (localVideoTrackID) hmsActions.setEnabledTrack(localVideoTrackID, false);
      if (localAudioTrackID) hmsActions.setEnabledTrack(localAudioTrackID, false);
    }
  }, [isBroadcasting, isConnected, localVideoTrackID, localAudioTrackID, hmsActions]);

  // Separate cleanup effect
  useEffect(() => {
    return () => {
      if (isConnectedRef.current) {
        hmsActions.leave();
      }
    };
  }, [hmsActions]);

  if (!authToken) {
    return (
      <div style={styles.placeholder}>
        <p>Start the show to enable video streaming</p>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div style={styles.placeholder}>
        <p>Connecting to video...</p>
      </div>
    );
  }

  // Not broadcasting — show remote stream or placeholder
  if (!isBroadcasting) {
    return (
      <div style={styles.container}>
        {activeRemoteTrack ? (
          <>
            <video
              ref={remoteBlurRef}
              autoPlay
              muted
              playsInline
              aria-hidden="true"
              style={styles.blurredBg}
            />
            <video
              ref={remoteVideoRef}
              autoPlay
              muted
              playsInline
              style={styles.video}
            />
          </>
        ) : (
          <div style={styles.videoOff}>
            <p style={styles.videoOffText}>Video is live from another device</p>
          </div>
        )}
        <div style={styles.badge}>LIVE</div>
        {onClaimBroadcast && (
          <div style={styles.claimOverlay}>
            <button onClick={onClaimBroadcast} style={styles.claimButton}>
              Broadcast from this device
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {isVideoEnabled ? (
        <>
          <video
            ref={localBlurRef}
            autoPlay
            muted
            playsInline
            aria-hidden="true"
            style={styles.blurredBg}
          />
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            style={styles.video}
          />
        </>
      ) : (
        <div style={styles.videoOff}>
          <div style={styles.videoOffIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16.5 10c1 0 1.5.5 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5" />
              <path d="M7 7l10 10" />
              <path d="M3 3l18 18" />
              <path d="M14 14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" />
              <path d="M14 6a2 2 0 0 1 2 2v.5" />
            </svg>
          </div>
          <p style={styles.videoOffText}>Camera Off</p>
        </div>
      )}
      <div style={styles.badge}>LIVE</div>
    </div>
  );
}

// Export toggle functions for use by parent (SellerDashboard controls)
export function useVideoPreviewControls() {
  const hmsActions = useHMSActions();
  const isAudioEnabled = useHMSStore(selectIsLocalAudioEnabled);
  const isVideoEnabled = useHMSStore(selectIsLocalVideoEnabled);
  const localAudioTrackID = useHMSStore(selectLocalAudioTrackID);
  const localVideoTrackID = useHMSStore(selectLocalVideoTrackID);

  const toggleAudio = async () => {
    if (localAudioTrackID) {
      await hmsActions.setEnabledTrack(localAudioTrackID, !isAudioEnabled);
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrackID) {
      await hmsActions.setEnabledTrack(localVideoTrackID, !isVideoEnabled);
    }
  };

  return { isAudioEnabled, isVideoEnabled, toggleAudio, toggleVideo };
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: colors.gray[900],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  blurredBg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'blur(32px) brightness(0.7) saturate(1.1)',
    transform: 'scale(1.15)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    position: 'relative',
    zIndex: 1,
  },
  videoOff: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#636366',
    gap: '12px',
  },
  videoOffIcon: {
    opacity: 0.6,
  },
  videoOffText: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '500',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.gray[900],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#636366',
  },
  badge: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    backgroundColor: '#FF2D55',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
  },
  claimOverlay: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  claimButton: {
    padding: '8px 20px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
};
